import React, { useRef, useState } from 'react';
import Select, {
  ActionMeta,
  ControlProps,
  DropdownIndicatorProps,
  GroupBase as DefaultGroupBase,
  GroupHeadingProps,
  InputActionMeta,
  MenuPlacement,
  MenuPosition,
  MultiValueProps,
  OnChangeValue,
  OptionProps,
  StylesConfig,
  components as defaultComponents,
} from 'react-select';
import { DownChevronProps } from 'react-select/dist/declarations/src/components/indicators';
import { FilterOptionOption } from 'react-select/dist/declarations/src/filters';
import { useTheme } from 'styled-components';
import { useOutsideClick } from 'src/hooks/useOutsideClick';
import { FormState } from 'src/types';
import {
  SelectWrapper,
  controlStyle,
  menuListStyle,
  menuStyle,
  optionStyle,
  singleValueStyle,
  valueStyle,
} from './DropdownSelect.styles';
import {
  DefaultOption,
  DropdownChevron,
  DropdownControl,
  DropdownIndicator,
  DropdownMultiValue,
  DropdownOptions,
} from './components';

export type ParameterOptions = {
  pluralLabel?: string | number;
  label: string | number;
  value: string | number | boolean;
};

export interface DropdownSelectProps<
  Option extends DefaultOption = DefaultOption,
  GroupBase extends DefaultGroupBase<Option> = DefaultGroupBase<Option>,
> {
  options: Array<Option | GroupBase>;
  defaultValue?: Option | Array<Option>;
  value?: Option | Array<Option> | null;
  isMulti?: boolean;
  isSearchable?: boolean;
  fuzzySearch?: boolean;
  placeholder?: string;
  withBorder?: boolean;
  onChange: (value: OnChangeValue<Option, boolean>) => void;
  menuPlacement?: MenuPlacement;
  menuPosition?: MenuPosition;
  hasSelectAllOption?: boolean;
  defaultAllSelected?: boolean;
  hideNoOptionsMessage?: boolean;
  'data-testid'?: string;
  state?: FormState;
  buttonText?: string;
  buttonIcon?: string;
  downloadIcon?: boolean;
  buttonVariant?: 'primary' | 'secondary' | 'tertiary';
  highlightButtonWhenOpen?: boolean;
  routing?: boolean;
  key?: string;
  width?: string;
  menuHeight?: string;
  valueContainerMaxHeight?: string;
  $zIndex?: number;
  /**
   * Forces component to manually track and set menu open state.  Needed in some contexts,
   * e.g. in draggable elements, until react-select patched
   * see https://github.com/JedWatson/react-select/issues/5176
   */
  manualMenuOpen?: boolean;
  defaultMenuIsOpen?: boolean;
  disabled?: boolean;
  tabIndex?: number;
  forwardRef?: React.Ref<HTMLSelectElement>;
  dropdownButtonSize?: 'small' | 'medium' | 'large';
  hideDropdownIndicator?: boolean;
  components?: {
    Option?: React.FC<OptionProps<Option, boolean, GroupBase>>;
    GroupHeading?: React.FC<GroupHeadingProps<Option, boolean, GroupBase>>;
    MultiValue?: React.FC<MultiValueProps<Option, boolean, GroupBase>>;
  };
  controlShouldRenderValue?: boolean;
  alignRight?: boolean;
  onInputChange?: (value: string) => void;
  disableFilter?: boolean;
  isOptionDisabled?: (option: Option) => boolean;
}

export const DropdownSelect = <
  Option extends DefaultOption = DefaultOption,
  GroupBase extends DefaultGroupBase<Option> = DefaultGroupBase<Option>,
>({
  placeholder = '',
  withBorder = true,
  menuPlacement = 'auto',
  menuPosition = 'absolute',
  hasSelectAllOption = false,
  defaultAllSelected = false,
  isSearchable = false,
  fuzzySearch = false,
  onInputChange,
  disableFilter = false,
  state = FormState.DEFAULT,
  manualMenuOpen = false,
  key,
  disabled = false,
  tabIndex,
  dropdownButtonSize,
  hideDropdownIndicator,
  forwardRef,
  components,
  hideNoOptionsMessage = false,
  controlShouldRenderValue = true,
  $zIndex,
  ...props
}: DropdownSelectProps<Option, GroupBase>) => {
  const theme = useTheme();
  const [allSelected, setAllSelected] = useState<boolean>(defaultAllSelected);
  const [menuIsOpen, setMenuIsOpen] = useState(
    props.defaultMenuIsOpen ?? false,
  );

  const shouldUseManualOpenFlag = Boolean(
    props.buttonText || props.routing || manualMenuOpen,
  );

  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick(
    ref,
    () => {
      if (shouldUseManualOpenFlag) {
        setMenuIsOpen(false);
        return;
      }
      // Selecting an option remounts react-select's <input>, dropping focus to
      // <body>. Calling blur() on an already-unfocused element fires no blur
      // event, so onInputBlur (react-select's own close path) never runs.
      // Focusing first forces a real focus->blur transition to fire it. Only
      // do this if this dropdown's own menu is open, since the ref/listener
      // are now always attached and would otherwise fire (and steal focus)
      // for every closed DropdownSelect on the page on any outside click.
      const input = ref.current?.querySelector('input');
      if (input?.getAttribute('aria-expanded') !== 'true') {
        return;
      }
      input.focus();
      input.blur();
    },
    shouldUseManualOpenFlag && !menuIsOpen,
  );

  const isEmpty =
    props.value === null ||
    (Array.isArray(props.value) && props.value?.length === 0);

  const readOnly = state === FormState.READONLY;

  const withBorderDefaultStyles: StylesConfig<Option, boolean, GroupBase> = {
    container: (provided) => ({
      ...provided,
      width: props.alignRight ? 'fit-content' : provided.width,
    }),
    menu: (provided) => ({
      ...provided,
      ...menuStyle(props.width, props.alignRight, theme),
    }),
    menuList: (provided) => ({
      ...provided,
      ...menuListStyle(props.menuHeight),
    }),
    control: (_, { menuIsOpen, ...provided }) => ({
      ...controlStyle(
        menuIsOpen || (readOnly && provided.isFocused),
        state,
        Boolean(props.isMulti),
        isEmpty,
        props.width,
        theme,
      ),
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      transition: 'all .2s ease',
      padding: 4,
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : undefined,
      alignSelf: 'auto',
      marginTop: props.isMulti && !isEmpty ? '2px' : '0px',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      display: hideDropdownIndicator ? 'none' : provided.display,
    }),
    option: (provided, { isFocused, isSelected, isDisabled }) => ({
      ...provided,
      ...optionStyle(
        isFocused,
        isSelected,
        Boolean(components?.Option),
        isDisabled,
        Boolean(props.isMulti),
        theme,
      ),
      ':active': {
        backgroundColor:
          isSelected && !props.isMulti
            ? theme.atlas.semantic.color.selection.secondary.active
            : theme.atlas.semantic.color.action.secondary.activeFill,
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      ...singleValueStyle(state, controlShouldRenderValue, theme),
    }),
    valueContainer: (provided) => ({
      ...provided,
      ...valueStyle(state, controlShouldRenderValue, theme),
      ...(props.valueContainerMaxHeight
        ? {
            maxHeight: props.valueContainerMaxHeight,
            overflowY: 'auto' as const,
          }
        : {}),
    }),
    placeholder: (provided) => ({
      ...provided,
      color: theme.atlas.semantic.color.type.muted,
    }),
    groupHeading: (provided) => ({
      ...provided,
      margin: 0,
      padding: '10px 20px',
      display: 'flex',
      alignItems: 'center',
    }),
    group: (provided) => ({
      ...provided,
      padding: 0,
    }),
  };

  const noBorderDefaultStyles: StylesConfig<Option, boolean, GroupBase> = {
    ...withBorderDefaultStyles,
    control: (_, { isFocused }) => ({
      ...controlStyle(
        isFocused,
        FormState.DEFAULT,
        Boolean(props.isMulti),
        isEmpty,
        props.width,
        theme,
      ),
      border: 'none',
      outline: 'none',
    }),
    menu: (provided) => ({
      ...provided,
      width: props.width ?? 'max-content',
      minWidth: props.width ?? '100%',
    }),
  };

  const styles: StylesConfig<Option, boolean, GroupBase> = withBorder
    ? withBorderDefaultStyles
    : noBorderDefaultStyles;

  const onChangeWithSelectAllOption = (
    selectedOptions: OnChangeValue<Option, boolean>,
    meta: ActionMeta<Option>,
  ) => {
    if (selectedOptions && Array.isArray(selectedOptions)) {
      if (meta.action === 'select-option' && meta.option?.value === 'all') {
        let allOptions: Option[] = [];
        if ('value' in props.options[0]) {
          allOptions = props.options as Option[];
        } else {
          allOptions = props.options.flatMap(
            (group) => (group as GroupBase).options,
          );
        }
        setAllSelected(true);
        return props.onChange(allOptions);
      }

      if (meta.action === 'deselect-option' && meta.option?.value === 'all') {
        setAllSelected(false);
        return props.onChange([]);
      }

      setAllSelected(
        meta.action === 'select-option' &&
          selectedOptions.length === props.options.length,
      );
      const newOptions = selectedOptions.filter(
        (option) => option.value !== 'all',
      );
      props.onChange(newOptions);
    }
  };

  const selectAllOptions = [
    { label: 'Select All', value: 'all' } as Option,
    ...props.options,
  ];

  const selectAllCheckFn = (type: string, optionalCheck = true) => {
    const returnSelectAll = hasSelectAllOption && optionalCheck;
    if (returnSelectAll) {
      return selectAllOptions;
    }

    switch (type) {
      case 'value':
        return props.value;
      case 'options':
        return props.options;
      case 'default':
        return props.defaultValue;
      default:
        return selectAllOptions;
    }
  };

  const customFilter = (
    option: FilterOptionOption<Option>,
    searchText: string,
  ) => {
    const labelIncludesSearchTerm = option.label
      .toLowerCase()
      .includes(searchText.toLowerCase());
    return labelIncludesSearchTerm;
  };

  return (
    <SelectWrapper
      data-testid={props['data-testid']}
      ref={ref}
      disabled={disabled || readOnly}
      tabIndex={tabIndex}
      $zIndex={$zIndex}
      alignRight={props.alignRight}
    >
      <Select
        {...props}
        isDisabled={disabled || readOnly}
        buttonText={props.buttonText}
        routing={props.routing}
        closeMenuOnSelect={!props.isMulti}
        defaultValue={selectAllCheckFn('default', defaultAllSelected) as Option}
        isMulti={props.isMulti}
        isClearable={false}
        isSearchable={isSearchable}
        filterOption={
          disableFilter
            ? null
            : fuzzySearch
              ? (option: FilterOptionOption<Option>, searchText: string) =>
                  customFilter(option, searchText)
              : undefined
        }
        onInputChange={(value: string, actionMeta: InputActionMeta) => {
          if (actionMeta.action === 'input-change' && onInputChange) {
            onInputChange(value);
          }
        }}
        styles={styles}
        placeholder={placeholder}
        noOptionsMessage={hideNoOptionsMessage ? () => null : undefined}
        menuPlacement={menuPlacement}
        menuPosition={menuPosition}
        tabIndex={tabIndex}
        value={
          selectAllCheckFn('value', allSelected) as Option | Option[] | null
        }
        options={selectAllCheckFn('options') as Option[] | GroupBase[]}
        onChange={(
          selectedOptions: OnChangeValue<Option, boolean>,
          meta: ActionMeta<Option>,
        ) => {
          if (hasSelectAllOption) {
            onChangeWithSelectAllOption(selectedOptions, meta);
          } else {
            props.onChange(selectedOptions);
          }
        }}
        hideSelectedOptions={false}
        openMenuOnClick={!readOnly}
        // This is a hack to prevent the menu opening when the chevron is clicked in read only mode
        menuIsOpen={
          readOnly ? false : shouldUseManualOpenFlag ? menuIsOpen : undefined
        }
        {...(shouldUseManualOpenFlag
          ? {
              onMenuOpen: () => setMenuIsOpen(true),
              onMenuClose: () => setMenuIsOpen(false),
            }
          : {})}
        components={{
          IndicatorSeparator: () => null,
          MultiValue:
            components?.MultiValue ??
            ((defaultProps: MultiValueProps<Option, boolean, GroupBase>) => (
              <DropdownMultiValue {...defaultProps} readOnly={readOnly} />
            )),
          Option: components?.Option ?? DropdownOptions,
          DropdownIndicator: hideDropdownIndicator
            ? null
            : (
                dropdownProps: DropdownIndicatorProps<
                  Option,
                  boolean,
                  GroupBase
                >,
              ) => (
                <DropdownIndicator
                  {...dropdownProps}
                  buttonVariant={props.buttonVariant}
                />
              ),
          Control: (defaultProps: ControlProps<Option, boolean, GroupBase>) => (
            <DropdownControl
              {...defaultProps}
              tabIndex={tabIndex}
              size={dropdownButtonSize}
              buttonIcon={props.buttonIcon}
              downloadIcon={props.downloadIcon}
              buttonVariant={props.buttonVariant}
              highlightButtonWhenOpen={props.highlightButtonWhenOpen}
            />
          ),
          DownChevron: (defaultProps: DownChevronProps) => (
            <DropdownChevron {...defaultProps} tabIndex={tabIndex} />
          ),
          GroupHeading:
            components?.GroupHeading ?? defaultComponents.GroupHeading,
        }}
      />
    </SelectWrapper>
  );
};
